// src/pages/Sucursales.jsx
import { useEffect, useState } from "react";
import ReactSelect from "react-select";
import {
  listDepartamento012,
  listMunicipio013,
  listTipoEstablecimiento009,
} from "../api/hacienda";
import { listClientData } from "../api/clientes";
import { listSucursales, updateSucursales } from "../api/sucursales";
import Loader from "../components/Loader";
import { FiEdit3, FiSave } from "react-icons/fi";

const bgColors = [
  "bg-red-200",
  "bg-blue-200",
  "bg-red-200",
  "bg-blue-200",
  "bg-red-200",
  "bg-blue-200",
  "bg-red-200",
];

const Sucursales = () => {
  const [loading, setLoading] = useState(true);
  const [departamentos, setDepartamentos] = useState([]);
  const [municipios, setMunicipios] = useState([]);
  const [municipiosFiltrados, setMunicipiosFiltrados] = useState({});
  const [establecimiento, setEstablecimientos] = useState([]);
  const [cliente, setCliente] = useState(null);
  const [sucursalesEdit, setSucursales] = useState([]);
  const [editingIndex, setEditingIndex] = useState(null);
  const [updatingIndex, setUpdatingIndex] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const dep = await listDepartamento012({});
        setDepartamentos(dep.data);

        const mun = await listMunicipio013({});
        setMunicipios(mun.data);

        const suc = await listSucursales({});
        setSucursales(suc.data);

        // Inicializar los municipios filtrados por sucursal usando id_departamentoFact
        const filtradosIniciales = {};
        suc.data.forEach((s, index) => {
          filtradosIniciales[index] = mun.data.filter(
            (m) => m.id_departamentoFact === s.id_departamento
          );
        });
        setMunicipiosFiltrados(filtradosIniciales);

        const est = await listTipoEstablecimiento009({});
        setEstablecimientos(est.data);

        const clienteData = await listClientData();
        setCliente(clienteData.data);
      } catch (error) {
        console.error("Error al obtener datos", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleSucursalChange = (index, field, value) => {
    const updated = [...sucursalesEdit];
    updated[index][field] = value;
    setSucursales(updated);

    // Si cambia el departamento, recalcular municipios (usando id_departamentoFact) y resetear id_municipio
    if (field === "id_departamento") {
      const municipiosFiltradosPorDep = municipios.filter(
        (m) => m.id_departamentoFact === value
      );
      setMunicipiosFiltrados((prev) => ({
        ...prev,
        [index]: municipiosFiltradosPorDep,
      }));

      updated[index].id_municipio = null;
      setSucursales(updated);
    }
  };

  const handleEditClick = async (index) => {
    if (editingIndex === index) {
      const sucursal = sucursalesEdit[index];
      const data = {
        nombre: sucursal.nombre,
        codigo_sucursal: sucursal.codigo_sucursal,
        id_typeSucursalFacts: sucursal.id_typeSucursalFacts,
        id_departamento: sucursal.id_departamento,
        id_municipio: sucursal.id_municipio,
        telefono: sucursal.telefono,
        correo: sucursal.correo,
        direccion: sucursal.direccion,
      };

      setUpdatingIndex(index);
      try {
        await updateSucursales(sucursal.id, data);
        setEditingIndex(null);
      } catch (error) {
        console.error("Error al actualizar", error);
      } finally {
        setUpdatingIndex(null);
      }
    } else {
      // Al habilitar edición, preparar municipios filtrados para ese índice
      const depId = sucursalesEdit[index].id_departamento;
      const municipiosFiltradosPorDep = municipios.filter(
        (m) => m.id_departamentoFact === depId
      );
      setMunicipiosFiltrados((prev) => ({
        ...prev,
        [index]: municipiosFiltradosPorDep,
      }));

      setEditingIndex(index);
    }
  };

  const isEditing = (index) => editingIndex === index;

  if (loading || !cliente) return <Loader />;

  const opcionesDepartamentos = departamentos.map((d) => ({
    value: d.id,
    label: `${d.codigo} | ${d.name}`,
  }));

  const opcionesEstablecimiento = establecimiento.map((es) => ({
    value: es.id,
    label: `${es.codigo} | ${es.name}`,
  }));

  return (
    <form onSubmit={(e) => e.preventDefault()} className="p-6 max-w-7xl mx-auto space-y-6">
      <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-6 gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">Gestión de Sucursales</h2>
          <p className="text-sm text-slate-500 mt-1">Ver y configurar las sucursales de la empresa</p>
        </div>
      </div>
      {sucursalesEdit.map((sucursal, index) => {
        const selectedDep = opcionesDepartamentos.find(
          (d) => d.value === sucursal.id_departamento
        );
        const selectedEst = opcionesEstablecimiento.find(
          (es) => es.value === sucursal.id_typeSucursalFacts
        );

        // Municipios disponibles por el departamento de la sucursal (id_departamentoFact)
        const baseDisponibles =
          municipiosFiltrados[index] ||
          municipios.filter(
            (m) => m.id_departamentoFact === sucursal.id_departamento
          );

        // Opciones para el select
        let opcionesMunicipios = baseDisponibles.map((m) => ({
          value: m.id,
          label: `${m.codigo} | ${m.name}`,
        }));

        // Fallback: si hay id_municipio pero no está en opciones (por cambios de datos),
        // lo agregamos temporalmente para que ReactSelect lo muestre
        if (
          sucursal.id_municipio &&
          !opcionesMunicipios.some((o) => o.value === sucursal.id_municipio)
        ) {
          const mSel = municipios.find((m) => m.id === sucursal.id_municipio);
          if (mSel) {
            opcionesMunicipios = [
              { value: mSel.id, label: `${mSel.codigo} | ${mSel.name}` },
              ...opcionesMunicipios,
            ];
          }
        }

        const selectedMun = opcionesMunicipios.find(
          (m) => m.value === sucursal.id_municipio
        );

        const bgColor = bgColors[index % bgColors.length];

        return (
          <div
            key={sucursal.id}
            className="bg-white border border-slate-200 shadow-sm p-6 rounded-2xl mb-6 relative transition-all hover:shadow-md"
          >
            {/* Color accent band */}
            <div className={`absolute left-0 top-0 bottom-0 w-1.5 ${bgColor.replace('bg-', 'bg-').replace('200', '400')}`}></div>

            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6 gap-4 pl-3">
              <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                <span className="bg-slate-100 text-slate-600 px-2.5 py-1 rounded-md text-sm">#{index + 1}</span>
                {sucursal.nombre || "Sucursal"}
              </h3>
              {updatingIndex === index ? (
                <button
                  type="button"
                  className="flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium rounded-lg text-white bg-emerald-500 cursor-not-allowed shadow-none"
                  disabled
                >
                  <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                  Guardando...
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => handleEditClick(index)}
                  className={`flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${isEditing(index)
                    ? "bg-emerald-500 text-white hover:bg-emerald-600 shadow-sm"
                    : "bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 focus:ring-2 focus:ring-slate-200"
                    }`}
                >
                  {isEditing(index) ? <FiSave className="w-4 h-4" /> : <FiEdit3 className="w-4 h-4" />}
                  {isEditing(index) ? "Guardar Cambios" : "Editar Sucursal"}
                </button>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 pl-3">
              {[
                { label: "Nombre de Sucursal", key: "nombre" },
                { label: "Dirección", key: "direccion" },
                { label: "Teléfono", key: "telefono" },
                { label: "Correo Electrónico", key: "correo" },
                { label: "Código de Sucursal", key: "codigo_sucursal" },
              ].map(({ label, key }) => (
                <div key={key}>
                  <label className="block mb-2 text-sm font-semibold text-slate-700">{label}</label>
                  <input
                    type="text"
                    value={sucursal[key] || ""}
                    onChange={(e) =>
                      handleSucursalChange(index, key, e.target.value)
                    }
                    className={`w-full border rounded-lg px-4 py-2.5 transition-colors focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${isEditing(index) ? "border-slate-300 bg-white" : "border-slate-200 bg-slate-50 text-slate-500 cursor-not-allowed"
                      }`}
                    disabled={!isEditing(index)}
                  />
                </div>
              ))}

              <div>
                <label className="block mb-2 text-sm font-semibold text-slate-700">Departamento</label>
                <ReactSelect
                  options={opcionesDepartamentos}
                  value={selectedDep || null}
                  onChange={(selected) =>
                    handleSucursalChange(
                      index,
                      "id_departamento",
                      selected ? selected.value : null
                    )
                  }
                  isClearable
                  isSearchable
                  isDisabled={!isEditing(index)}
                  className="react-select-container"
                  classNamePrefix="react-select"
                  menuPortalTarget={document.body}
                  styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                />
              </div>

              <div>
                <label className="block mb-2 text-sm font-semibold text-slate-700">Establecimiento</label>
                <ReactSelect
                  options={opcionesEstablecimiento}
                  value={selectedEst || null}
                  onChange={(selected) =>
                    handleSucursalChange(
                      index,
                      "id_typeSucursalFacts",
                      selected ? selected.value : null
                    )
                  }
                  isClearable
                  isSearchable
                  isDisabled={!isEditing(index)}
                  className="react-select-container"
                  classNamePrefix="react-select"
                  menuPortalTarget={document.body}
                  styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                />
              </div>

              <div>
                <label className="block mb-2 text-sm font-semibold text-slate-700">Municipio</label>
                <ReactSelect
                  options={opcionesMunicipios}
                  value={selectedMun || null}
                  onChange={(selected) =>
                    handleSucursalChange(
                      index,
                      "id_municipio",
                      selected ? selected.value : null
                    )
                  }
                  isClearable
                  isSearchable
                  isDisabled={!isEditing(index)}
                  className="react-select-container"
                  classNamePrefix="react-select"
                  menuPortalTarget={document.body}
                  styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                  placeholder={
                    municipiosFiltrados[index]?.length
                      ? "Selecciona un municipio"
                      : "Sin municipios"
                  }
                />
              </div>
            </div>
          </div>
        );
      })}
    </form>
  );
};

export default Sucursales;
